/**
 * PATH: packages/ui/src/kanban/components/card-detail/useDrawerFocusFromDeepLink.ts
 * FILE_ID: UI-KANBAN-FOCUS-002
 * PURPOSE: Idempotent rAF/retry focus for mention deep-links inside the drawer.
 * WHY: Team Chat messages load async; a single scroll on mount often misses.
 *   Retries for ~2.5s then fails soft (card still open).
 * RELATED:
 *   - drawerFocusFromDeepLink.ts — pure selectors + scroll/highlight
 *   - CardDetailDrawer.tsx — ensures Card tab, then runs this hook
 */
import { useEffect, useRef } from 'react'

import {
  applyDrawerFocus,
  drawerFocusKey,
  isKanbanDrawerFocusSection,
  queryDrawerFocusTarget,
  type KanbanDrawerFocusSection,
} from './drawerFocusFromDeepLink'

const RETRY_MS = 80
const TIMEOUT_MS = 2500

export interface UseDrawerFocusFromDeepLinkArgs {
  open: boolean
  cardId: string | null | undefined
  focus: string | null | undefined
  entityId?: string | null
  /** Ensure Card tab (not Email/Files) before focusing. */
  ensureCardTab?: () => void
}

export function useDrawerFocusFromDeepLink({
  open,
  cardId,
  focus,
  entityId,
  ensureCardTab,
}: UseDrawerFocusFromDeepLinkArgs) {
  const appliedRef = useRef<string | null>(null)

  useEffect(() => {
    // Closing the drawer must clear the applied guard so a later reopen
    // (same ?card=&focus=&id= via notification) can scroll/highlight again.
    if (!open) {
      appliedRef.current = null
      return
    }
    if (!cardId) return
    if (!isKanbanDrawerFocusSection(focus)) return
    const key = drawerFocusKey(cardId, focus, entityId)
    if (!key || appliedRef.current === key) return

    ensureCardTab?.()

    const section = focus as KanbanDrawerFocusSection
    const started = Date.now()
    let cancelled = false
    let raf = 0
    let timer: ReturnType<typeof setTimeout> | undefined

    const tick = () => {
      if (cancelled) return
      const el = queryDrawerFocusTarget(document, section, entityId)
      if (el) {
        applyDrawerFocus(el)
        appliedRef.current = key
        return
      }
      if (Date.now() - started >= TIMEOUT_MS) {
        // Section never appeared (e.g. Team chat bridge unavailable) —
        // mark applied so we don't retry forever; card remains open.
        appliedRef.current = key
        return
      }
      timer = setTimeout(() => {
        raf = requestAnimationFrame(tick)
      }, RETRY_MS)
    }

    raf = requestAnimationFrame(tick)
    return () => {
      cancelled = true
      cancelAnimationFrame(raf)
      if (timer) clearTimeout(timer)
    }
  }, [open, cardId, focus, entityId, ensureCardTab])
}
